// Copyright (c) Docugami, Inc. All rights reserved.

import { ISpinnerStyles, IStyle, Modal, Spinner } from "@fluentui/react";
import { DgButton } from "components/common/DgCommonStyledComponents";
import { RegularButton, SmallButtonLabel } from "components/common/Fonts";
import { IconDescriptor, IconRenderer } from "components/work_path/IconRenderer";
import { useInterval } from "hooks/Interval";
import { useTimeout } from "hooks/Timeout";
import { OAuthRequestStatus } from "networking/Authentication";
import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

const ModalContentContainer = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    padding: 20px 15px 7px;
    box-sizing: border-box;
    align-items: center;
`

const SpinnerContainer = styled.div`
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    position: relative;
    margin: 45px 0px 35px;
`

const FooterContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 35px;
    flex-direction: column;
    gap: 5px;
`

const ModalButton = styled(DgButton)`
    display: flex;
    height: 100%;
    justify-content: center;
    align-items: center;
    font-size: 12px;
`

const ButtonContainer = styled.div`
    height: 25px;
    margin-top: auto;
`

enum ModalState {
    None,
    Authenticating,
    Success,
    Failed
};

const MinStateDuration: number = 1000;
const ShowCancelDuration: number = 20000;

interface OAuthModalProps {
    title: string;
    status: OAuthRequestStatus;
    onDismiss: () => void;
    onCancel: () => void;
    icon: IconDescriptor;
    autoDismiss?: boolean;
}

export const OAuthModal = (props: OAuthModalProps): JSX.Element => {
    const [currentState, setCurrentState] = useState(ModalState.None);
    const [canTransition, setCanTransition] = useState(true);
    const [showCancel, setShowCancel] = useState(false);
    const isActive = currentState !== ModalState.None;

    useInterval(() => setCanTransition(true), !canTransition && isActive ? MinStateDuration : undefined);
    useTimeout(() => setShowCancel(true), isActive ? ShowCancelDuration : undefined);

    useEffect(() => {
        if (canTransition) {
            const newState = getNextState(currentState, props.status, !!props.autoDismiss);
            if (newState !== currentState) {
                setCanTransition(false);
                setCurrentState(newState);
            }
        }
    }, [props.status, canTransition, currentState, props.autoDismiss]);

    const mainStyle: Partial<IStyle> = {
        display: "flex",
        minWidth: "200px",
        minHeight: "200px",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        borderRadius: "10px"
    }

    const contentStyle: Partial<IStyle> = {
        display: "flex",
        overflow: "hidden",
        width: "100%",
        height: "100%"
    }

    const spinnerStyle: Partial<ISpinnerStyles> = {
        root: {
            position: "absolute"
        },
        circle: {
            width: "110px",
            height: "110px",
            borderWidth: "4px"
        }
    }

    function getModalButton(): JSX.Element | null {
        if (currentState === ModalState.Authenticating && showCancel) {
            return <ModalButton
                onClick={() => {
                    setCurrentState(ModalState.None);
                    props.onCancel();
                }}>CANCEL</ModalButton>

        }
        else if (currentState === ModalState.Success || currentState === ModalState.Failed) {
            return <ModalButton
                onClick={() => {
                    setCurrentState(ModalState.None);
                    props.onDismiss();
                }}>OK</ModalButton>
        }

        return null;
    }

    const statusLabel = useMemo(() => {
        switch (currentState) {
            case ModalState.None: {
                return '';
            }
            case ModalState.Authenticating: {
                return 'Authenticating...'
            }
            case ModalState.Failed: {
                return 'Uh oh! Something went wrong. Please try again later.'
            }
            case ModalState.Success: {
                return "Success!";
            }
        }
    }, [currentState]);

    return (
        <Modal
            isBlocking={currentState === ModalState.Authenticating}
            isOpen={currentState !== ModalState.None}
            styles={{ main: mainStyle, scrollableContent: contentStyle }}>
            <ModalContentContainer>
                <RegularButton>{props.title}</RegularButton>
                <SpinnerContainer>
                    <IconRenderer source={props.icon} width={75} height={75} containerHeight={75} containerWidth={75} />
                    {currentState === ModalState.Authenticating && <Spinner styles={spinnerStyle} />}
                </SpinnerContainer>
                <FooterContainer>
                    <SmallButtonLabel>{statusLabel}</SmallButtonLabel>
                    <ButtonContainer>
                        {getModalButton()}
                    </ButtonContainer>
                </FooterContainer>
            </ModalContentContainer>
        </Modal>
    );
}

function getNextState(currentState: ModalState, status: OAuthRequestStatus, autoDismiss: boolean = false): ModalState {
    switch (status) {
        case OAuthRequestStatus.None: {
            if (!autoDismiss && (currentState === ModalState.Success || currentState === ModalState.Failed)) {
                // User must manually dismiss the modal
                return currentState;
            }
            else {
                return ModalState.None;
            }
        }

        case OAuthRequestStatus.Initiated:
        case OAuthRequestStatus.Authorizing: {
            return ModalState.Authenticating;
        }

        case OAuthRequestStatus.Succeeded: {
            return ModalState.Success
        }

        case OAuthRequestStatus.Failed:
        case OAuthRequestStatus.Timeout: {
            return ModalState.Failed;
        }

        default:
            throw new Error("Unhandled auth status");
    }
}
